import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import { createElement, Fragment, type ReactNode } from "react";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Parse BBCode-style markup and return React elements
 * Supported tags:
 * - [b]text[/b] - bold
 * - [i]text[/i] - italic
 * - [u]text[/u] - underline
 * - [s]text[/s] - strikethrough
 * - [color=#hex]text[/color] - text color
 * - [bg=#hex]text[/bg] - background color (highlight)
 * 
 * Tags can be nested: [b][color=#ff0000]Bold red[/color][/b]
 */
export function parseBBCode(text: string): ReactNode {
  if (!text) return text;
  
  // Check if text contains any BBCode tags
  if (!/\[(?:b|i|u|s|color|bg)(?:=[^\]]+)?\]/i.test(text)) {
    return text;
  }
  
  const elements: ReactNode[] = [];
  let key = 0;
  
  // Regex to match BBCode tags
  const tagRegex = /\[(b|i|u|s|color|bg)(=[^\]]+)?\]([\s\S]*?)\[\/\1\]/gi;
  
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  
  while ((match = tagRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      elements.push(text.slice(lastIndex, match.index));
    }
    
    const [fullMatch, tag, param, content] = match;
    const tagLower = tag.toLowerCase();
    const paramValue = param ? param.slice(1) : null; // Remove the '=' prefix
    
    // Parse content recursively for nested tags
    const parsedContent = parseBBCode(content);
    
    // Create the styled element
    let style: React.CSSProperties = {};
    let className = "";
    
    switch (tagLower) {
      case "b":
        className = "font-bold";
        break;
      case "i":
        className = "italic";
        break;
      case "u":
        className = "underline";
        break;
      case "s":
        className = "line-through";
        break;
      case "color":
        if (paramValue) {
          style.color = paramValue;
        }
        break;
      case "bg":
        if (paramValue) {
          style.backgroundColor = paramValue;
          style.padding = "0 2px";
          style.borderRadius = "2px";
        }
        break;
    }
    
    elements.push(
      createElement(
        "span",
        { 
          key: key++, 
          className: className || undefined,
          style: Object.keys(style).length > 0 ? style : undefined 
        },
        parsedContent
      )
    );
    
    lastIndex = match.index + fullMatch.length;
  }
  
  // Add remaining text after last match
  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }
  
  // If no matches were found, return original text
  if (elements.length === 0) {
    return text;
  }
  
  // If only one element and it's a string, return it directly
  if (elements.length === 1 && typeof elements[0] === "string") {
    return elements[0];
  }
  
  return createElement(Fragment, null, ...elements);
}

/**
 * Detect URLs (http/https) in plain text and render them as clickable links.
 * Returns the original text untouched when no URL is found, so it's safe to
 * apply to any free-form string (tooltips, helper texts, etc.) without
 * changing its layout.
 *
 * Note: trailing punctuation (`.`, `,`, `;`, `:`, `!`, `?`, `)`, `]`) directly
 * attached to the URL is excluded from the href so a URL ending a sentence
 * still produces a working link.
 */
export function linkifyText(text: string | null | undefined): ReactNode {
  if (!text) return text ?? null;

  const urlRegex = /https?:\/\/[^\s]+/gi;
  if (!urlRegex.test(text)) {
    return text;
  }

  // Reset regex state after the .test() above (g flag is stateful).
  urlRegex.lastIndex = 0;

  const elements: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = urlRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      elements.push(text.slice(lastIndex, match.index));
    }

    let url = match[0];
    let trailing = "";
    while (url.length > 0 && /[.,;:!?)\]]/.test(url[url.length - 1])) {
      trailing = url[url.length - 1] + trailing;
      url = url.slice(0, -1);
    }

    elements.push(
      createElement(
        "a",
        {
          key: key++,
          href: url,
          target: "_blank",
          rel: "noopener noreferrer",
          className: "underline text-primary break-all",
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        },
        url
      )
    );

    if (trailing) {
      elements.push(trailing);
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    elements.push(text.slice(lastIndex));
  }

  if (elements.length === 1 && typeof elements[0] === "string") {
    return elements[0];
  }

  return createElement(Fragment, null, ...elements);
}

/**
 * Strip BBCode tags from text and return plain text
 * Useful for error messages, logs, or other contexts where formatting is not supported
 */
export function stripBBCode(text: string): string {
  if (!text) return text;
  
  // Remove all BBCode tags, keeping only the content
  return text.replace(/\[(b|i|u|s|color|bg)(=[^\]]+)?\]([\s\S]*?)\[\/\1\]/gi, "$3");
}

export function formatDate(date: string | Date): string {
  // Si c'est une chaîne ISO sans timezone, on assume qu'elle est en UTC
  let d: Date;
  if (typeof date === "string" && !date.endsWith("Z") && !date.includes("+")) {
    d = new Date(date + "Z");
  } else {
    d = new Date(date);
  }
  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function formatNumber(value: number, decimals = 2): string {
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
  }).format(value);
}

export function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/(^_|_$)+/g, "");
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

